import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from '../client';
import { queryKeys } from '../query-keys';
import type { HouseholdMember } from '@pantry/shared';
import type {
  HouseholdWithMembers,
  CreateHouseholdData,
  UpdateHouseholdData,
  MemberWithUser,
  UpdateMemberRoleData,
  InvitationWithInviter,
  CreateInvitationData,
  AcceptInvitationData,
  InvitationPreview,
  TransferOwnershipResult,
} from '../types';

/**
 * Fetch all households the current user is a member of.
 */
async function getHouseholds(): Promise<HouseholdWithMembers[]> {
  return apiFetch<HouseholdWithMembers[]>('/households');
}

/**
 * Fetch a single household by ID.
 */
async function getHousehold(id: string): Promise<HouseholdWithMembers> {
  return apiFetch<HouseholdWithMembers>(`/households/${id}`);
}

/**
 * Create a new household.
 */
async function createHousehold(data: CreateHouseholdData): Promise<HouseholdWithMembers> {
  return apiFetch<HouseholdWithMembers>('/households', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

/**
 * Update an existing household.
 */
async function updateHousehold(
  id: string,
  data: UpdateHouseholdData
): Promise<HouseholdWithMembers> {
  return apiFetch<HouseholdWithMembers>(`/households/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  });
}

/**
 * Delete a household.
 */
async function deleteHousehold(id: string): Promise<void> {
  await apiFetch(`/households/${id}`, { method: 'DELETE' });
}

/**
 * Hook to fetch all households the user is a member of.
 * Uses a 2 minute stale time for infrequent updates.
 */
export function useHouseholds() {
  return useQuery({
    queryKey: queryKeys.households.list(),
    queryFn: getHouseholds,
    staleTime: 2 * 60 * 1000, // 2 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });
}

/**
 * Hook to fetch a single household by ID.
 * Uses a 1 minute stale time as it may have more activity.
 */
export function useHousehold(id: string) {
  return useQuery({
    queryKey: queryKeys.households.detail(id),
    queryFn: () => getHousehold(id),
    enabled: !!id,
    staleTime: 1 * 60 * 1000, // 1 minute
    gcTime: 10 * 60 * 1000, // 10 minutes
  });
}

/**
 * Hook to create a new household.
 * Invalidates the households list on success.
 */
export function useCreateHousehold() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createHousehold,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.households.all });
    },
  });
}

/**
 * Hook to update a household.
 * Updates both the detail cache and invalidates the list.
 */
export function useUpdateHousehold() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateHouseholdData }) =>
      updateHousehold(id, data),
    onSuccess: (updatedHousehold, { id }) => {
      queryClient.setQueryData(queryKeys.households.detail(id), updatedHousehold);
      queryClient.invalidateQueries({ queryKey: queryKeys.households.list() });
    },
  });
}

/**
 * Hook to delete a household.
 * Invalidates all household queries on success.
 */
export function useDeleteHousehold() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteHousehold,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.households.all });
    },
  });
}

// ============================================================================
// Member Management
// ============================================================================

/**
 * Fetch all members of a household.
 */
async function getHouseholdMembers(householdId: string): Promise<MemberWithUser[]> {
  return apiFetch<MemberWithUser[]>(`/households/${householdId}/members`);
}

/**
 * Update a member's role.
 */
async function updateMemberRole(
  householdId: string,
  userId: string,
  data: UpdateMemberRoleData
): Promise<MemberWithUser> {
  return apiFetch<MemberWithUser>(`/households/${householdId}/members/${userId}`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  });
}

/**
 * Remove a member from a household.
 */
async function removeMember(householdId: string, userId: string): Promise<void> {
  await apiFetch(`/households/${householdId}/members/${userId}`, { method: 'DELETE' });
}

/**
 * Leave a household.
 */
async function leaveHousehold(householdId: string): Promise<void> {
  await apiFetch(`/households/${householdId}/leave`, { method: 'POST' });
}

/**
 * Transfer ownership to another member.
 */
async function transferOwnership(
  householdId: string,
  newOwnerId: string
): Promise<TransferOwnershipResult> {
  return apiFetch<TransferOwnershipResult>(
    `/households/${householdId}/transfer-ownership/${newOwnerId}`,
    { method: 'POST' }
  );
}

/**
 * Hook to fetch all members of a household.
 */
export function useHouseholdMembers(householdId: string) {
  return useQuery({
    queryKey: queryKeys.households.members(householdId),
    queryFn: () => getHouseholdMembers(householdId),
    enabled: !!householdId,
    staleTime: 1 * 60 * 1000, // 1 minute
  });
}

/**
 * Hook to update a member's role.
 * Invalidates members list on success.
 */
export function useUpdateMemberRole(householdId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ userId, role }: { userId: string; role: UpdateMemberRoleData['role'] }) =>
      updateMemberRole(householdId, userId, { role }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.households.members(householdId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.households.detail(householdId) });
    },
  });
}

/**
 * Hook to remove a member from a household.
 */
export function useRemoveMember(householdId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (userId: string) => removeMember(householdId, userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.households.members(householdId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.households.detail(householdId) });
    },
  });
}

/**
 * Hook to leave a household.
 */
export function useLeaveHousehold() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: leaveHousehold,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.households.all });
    },
  });
}

/**
 * Hook to transfer ownership to another member.
 */
export function useTransferOwnership(householdId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (newOwnerId: string) => transferOwnership(householdId, newOwnerId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.households.members(householdId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.households.detail(householdId) });
    },
  });
}

// ============================================================================
// Invitation Management
// ============================================================================

/**
 * Fetch all pending invitations for a household.
 */
async function getHouseholdInvitations(householdId: string): Promise<InvitationWithInviter[]> {
  return apiFetch<InvitationWithInviter[]>(`/households/${householdId}/invitations`);
}

/**
 * Create a new invitation.
 */
async function createInvitation(
  householdId: string,
  data: CreateInvitationData
): Promise<InvitationWithInviter> {
  return apiFetch<InvitationWithInviter>(`/households/${householdId}/invitations`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

/**
 * Cancel a pending invitation.
 */
async function cancelInvitation(householdId: string, invitationId: string): Promise<void> {
  await apiFetch(`/households/${householdId}/invitations/${invitationId}`, { method: 'DELETE' });
}

/**
 * Resend an invitation email.
 */
async function resendInvitation(householdId: string, invitationId: string): Promise<void> {
  await apiFetch(`/households/${householdId}/invitations/${invitationId}/resend`, {
    method: 'POST',
  });
}

/**
 * Preview an invitation by token (for accept page).
 */
async function previewInvitation(token: string): Promise<InvitationPreview> {
  return apiFetch<InvitationPreview>(`/invitations/preview/${token}`);
}

/**
 * Accept an invitation.
 */
async function acceptInvitation(data: AcceptInvitationData): Promise<HouseholdMember> {
  return apiFetch<HouseholdMember>('/invitations/accept', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

/**
 * Hook to fetch pending invitations for a household.
 */
export function useHouseholdInvitations(householdId: string) {
  return useQuery({
    queryKey: queryKeys.households.invitations(householdId),
    queryFn: () => getHouseholdInvitations(householdId),
    enabled: !!householdId,
    staleTime: 30 * 1000, // 30 seconds (invitations may be accepted quickly)
  });
}

/**
 * Hook to create a new invitation.
 */
export function useCreateInvitation(householdId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateInvitationData) => createInvitation(householdId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.households.invitations(householdId) });
    },
  });
}

/**
 * Hook to cancel a pending invitation.
 */
export function useCancelInvitation(householdId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (invitationId: string) => cancelInvitation(householdId, invitationId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.households.invitations(householdId) });
    },
  });
}

/**
 * Hook to resend an invitation email.
 */
export function useResendInvitation(householdId: string) {
  return useMutation({
    mutationFn: (invitationId: string) => resendInvitation(householdId, invitationId),
  });
}

/**
 * Hook to preview an invitation by token.
 */
export function useInvitationPreview(token: string, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: queryKeys.invitations.preview(token),
    queryFn: () => previewInvitation(token),
    enabled: options?.enabled !== false && !!token,
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: false, // Don't retry on invalid tokens
  });
}

/**
 * Hook to accept an invitation.
 */
export function useAcceptInvitation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: acceptInvitation,
    onSuccess: () => {
      // Invalidate all household queries as user may now have access to a new household
      queryClient.invalidateQueries({ queryKey: queryKeys.households.all });
    },
  });
}
